import React, { FC, memo } from 'react';

import Image from 'next/image';

import { useBreakpoint } from '@contexts/breakpoints';
import ListingItemSlider, {
  SliderImage,
} from '@components/ui/listing/ListingItemSlider';
import ImageWithFallback from '@components/utils/ImageWithFallback';
import { optimizeImage } from '@utils/imageOptimization';

import {
  StyledCol,
  MainImageWrapper,
  LastImage,
  ViewGalleryButton,
  StyledRow,
  BottomImage,
  ShowAllImagesText,
  MainImage,
} from './MultipleImageViewStyles';

const FALLBACK_IMAGE_URL = '/static/images/setur-static.webp';

export interface Images {
  url?: string;
  alt?: string;
  placeholderUrl?: string | null;
  providerId?: number | null;
}
export interface MultipleImageViewProps {
  images: Images[];
  isSuggested?: boolean;
  isSafe?: boolean;
  totalImages: number;
  viewGallery?: () => void;
  isGalleryOpen?: boolean;
}

const formatSliderImage = (images: Images[]): SliderImage[] =>
  images.map((image) => ({
    imageUrl: image.url || '',
    alt: image.alt || 'S&F',
    providerId: image.providerId,
    placeholderUrl: image.placeholderUrl,
  }));

const MultipleImageView: FC<MultipleImageViewProps> = ({
  images,
  totalImages,
  viewGallery,
  isGalleryOpen = false,
}) => {
  const { xl } = useBreakpoint();

  const openGalleryHandler = () => viewGallery?.();

  if (images?.length === 0) {
    return null;
  }

  return (
    <StyledRow
      onClick={xl ? viewGallery : undefined}
      data-testid='viewGalleryModal'
    >
      <MainImageWrapper>
        {xl || xl === null ? (
          <ImageWithFallback
            Component={MainImage}
            src={optimizeImage(
              images?.[0]?.url ?? FALLBACK_IMAGE_URL,
              '1024',
              '768',
              {
                providerId: images?.[0]?.providerId,
                placeholderUrl: images?.[0]?.placeholderUrl,
              }
            )}
            fallbackImage={FALLBACK_IMAGE_URL}
            checkInitialLoad
            alt={images?.[0]?.alt || 'S&F'}
            style={{ borderRadius: 8 }}
          />
        ) : (
          <ListingItemSlider
            images={formatSliderImage(images || [])}
            onGalleryView={viewGallery}
            fallBackImage={FALLBACK_IMAGE_URL}
            imageStyle={{
              borderRadius: 0,
              height: 206,
              maxHeight: 206,
            }}
          />
        )}
      </MainImageWrapper>

      {xl || xl === null ? (
        <>
          <StyledCol>
            <ImageWithFallback
              Component={Image}
              width={288}
              height={183}
              src={
                optimizeImage(images?.[1]?.url, '512', '384', {
                  providerId: images?.[1]?.providerId,
                  placeholderUrl: images?.[1]?.placeholderUrl,
                }) || FALLBACK_IMAGE_URL
              }
              alt={images?.[1]?.alt || 'S&F'}
              style={{ borderRadius: 8 }}
            />
            <BottomImage>
              <ImageWithFallback
                Component={Image}
                width={288}
                height={183}
                src={
                  optimizeImage(images?.[2]?.url, '512', '384', {
                    providerId: images?.[2]?.providerId,
                    placeholderUrl: images?.[2]?.placeholderUrl,
                  }) || FALLBACK_IMAGE_URL
                }
                alt={images?.[2]?.alt || 'S&F'}
                style={{ borderRadius: 8 }}
              />
            </BottomImage>
          </StyledCol>

          <StyledCol>
            <ImageWithFallback
              Component={Image}
              width={288}
              height={183}
              src={
                optimizeImage(images?.[3]?.url, '512', '384', {
                  providerId: images?.[3]?.providerId,
                  placeholderUrl: images?.[3]?.placeholderUrl,
                }) || FALLBACK_IMAGE_URL
              }
              alt={images?.[3]?.alt || 'S&F'}
              style={{ borderRadius: 8 }}
            />
            <LastImage>
              <ShowAllImagesText>Tüm Fotoğrafları Gör</ShowAllImagesText>
              <ImageWithFallback
                Component={Image}
                width={288}
                height={183}
                src={
                  optimizeImage(images?.[4]?.url, '512', '384', {
                    providerId: images?.[4]?.providerId,
                    placeholderUrl: images?.[4]?.placeholderUrl,
                  }) || FALLBACK_IMAGE_URL
                }
                alt={images?.[4]?.alt || 'S&F'}
                style={{ borderRadius: 8 }}
              />
            </LastImage>
          </StyledCol>
        </>
      ) : null}

      {isGalleryOpen ? (
        <ViewGalleryButton onClick={openGalleryHandler}>
          Tüm Fotoğrafları Gör ({totalImages})
        </ViewGalleryButton>
      ) : null}
    </StyledRow>
  );
};

export default memo(MultipleImageView);
